import { useState } from "react";
import Modal from "@/components/base/Modal";
import InvoicePreview from "@/components/feature/InvoicePreview";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { useInvoicePreview } from "@/hooks/useInvoicePreview";
import { refundInvoice, voidInvoice } from "@/lib/salesApi";
import { logAudit } from "@/lib/audit";
import { formatDateTime, formatMoney } from "@/lib/format";
import { STATUS_LABELS, STATUS_STYLES } from "@/lib/invoiceTemplate";

interface InvoiceDetailModalProps {
  invoiceId: string | null;
  onClose: () => void;
  onChanged: () => void;
}

type ActionKind = "void" | "refund" | null;

export default function InvoiceDetailModal({ invoiceId, onClose, onChanged }: InvoiceDetailModalProps) {
  const toast = useToast();
  const { profile, isAdmin, hasPermission } = useAuth();
  const { data, loading, error, printer } = useInvoicePreview(invoiceId);
  const [action, setAction] = useState<ActionKind>(null);
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);

  const invoice = data?.invoice ?? null;
  const isPaid = invoice?.status === "paid";
  const canVoid = isAdmin || hasPermission("sales.void");
  const canRefund = isAdmin || hasPermission("refunds.create");

  const statusLabel = invoice?.status ? STATUS_LABELS[invoice.status] ?? invoice.status : "-";
  const statusStyle = invoice?.status ? STATUS_STYLES[invoice.status] ?? "bg-background-200 text-foreground-600" : "";

  const runAction = async () => {
    if (!invoice || !action) return;
    setWorking(true);
    try {
      const result =
        action === "void"
          ? await voidInvoice(invoice.id, reason.trim() || "Anulación de factura")
          : await refundInvoice(invoice.id, reason.trim() || "Reembolso de factura");

      if (!result.ok) {
        toast.error(result.error ?? "No se pudo completar la operación.");
        return;
      }

      await logAudit({
        userId: profile?.id ?? null,
        userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
        action: action === "void" ? "anular" : "reembolsar",
        entity: "invoices",
        entityId: invoice.id,
        branchId: invoice.branch_id,
        oldValue: { status: invoice.status, total: invoice.total },
        newValue: { status: action === "void" ? "void" : "refunded", reason: reason.trim() || null },
      });

      toast.success(action === "void" ? "Factura anulada." : "Factura reembolsada.");
      setAction(null);
      setReason("");
      onChanged();
      onClose();
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <Modal
        open={!!invoiceId}
        onClose={onClose}
        title="Detalle de factura"
        subtitle={invoice ? `${invoice.number ?? ""} · ${formatDateTime(invoice.created_at)}` : undefined}
        size="lg"
        footer={
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            {isPaid && canRefund ? (
              <button
                type="button"
                onClick={() => setAction("refund")}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-100"
              >
                <i className="ri-arrow-go-back-line text-base"></i>
                Reembolsar
              </button>
            ) : null}
            {isPaid && canVoid ? (
              <button
                type="button"
                onClick={() => setAction("void")}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
              >
                <i className="ri-forbid-2-line text-base"></i>
                Anular
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              Cerrar
            </button>
          </div>
        }
      >
        {invoice ? (
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyle}`}>{statusLabel}</span>
            <span className="text-sm text-foreground-600">
              Total: <strong className="text-foreground-950">{formatMoney(invoice.total)}</strong>
            </span>
            {data?.customer?.name ? (
              <span className="text-sm text-foreground-600">Cliente: {data.customer.name}</span>
            ) : null}
          </div>
        ) : null}

        <InvoicePreview
          data={data}
          loading={loading}
          error={error}
          posWidth={printer.pos_width}
          printer={printer}
        />
      </Modal>

      <Modal
        open={!!action}
        onClose={() => {
          setAction(null);
          setReason("");
        }}
        title={action === "void" ? "Anular factura" : "Reembolsar factura"}
        subtitle={
          action === "void"
            ? "La factura quedará anulada y el inventario de los productos vendidos se reintegrará."
            : "Se registrará el reembolso y el inventario de los productos vendidos se reintegrará."
        }
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setAction(null);
                setReason("");
              }}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={runAction}
              disabled={working}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60"
            >
              {working ? (
                <i className="ri-loader-4-line animate-spin text-base"></i>
              ) : (
                <i className="ri-check-line text-base"></i>
              )}
              Confirmar
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-2">
          <label className="text-xs font-semibold text-foreground-700">Motivo (opcional)</label>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            placeholder="Describe el motivo de la anulación o el reembolso"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
          />
        </div>
      </Modal>
    </>
  );
}